import { useMemo, useState } from "react";
import { useTable } from "react-table";
import { defaultDateDisplay, formatCurrency } from "../../../../helpers";
import primaryComponents from "../../../primaryComponents";
import { Link, useNavigate } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import { DeleteIcon } from "../../../../assets/svgIcons";
import "../../../../assets/styles/base/globalPage.scss";
import { BillsExtra } from "../BillsExtra";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import secondaryComponents from "../..";

export const useBillsTable = ({ bills = [] }) => {
  const navigate = useNavigate();
  const [selectedBillsDetails, setSelectedBillsDetails] = useState(null);

  const columns = useMemo(
    () => [
      {
        Header: "Created At",
        accessor: "created_at",
        Cell: ({ cell: { value } }) =>
          value ? defaultDateDisplay(value) : "N/A",
      },
      {
        Header: "Bill Number",
        accessor: "bill_number",
        Cell: ({ cell: { value }, row }) => (
          <Link
            to={pathConstants.INVENTORY_BILLS_DETAILS({
              id: row?.original?.id,
            })}
            className="purple--text smallest-text-size"
          >
            {value || "N/A"}
          </Link>
        ),
      },
      {
        Header: "PO Number",
        accessor: "purchase_order",
        Cell: ({ cell: { value } }) => (
          <Link
            to={pathConstants.INVENTORY_PURCHASE_ORDER_DETAILS({
              id: value?.id,
            })}
            className="purple--text smallest-text-size"
          >
            {value?.po_number || "N/A"}
          </Link>
        ),
      },
      {
        Header: "Supplier",
        accessor: "supplier",
        Cell: ({ cell: { value } }) => (
          <Link
            to={pathConstants.INVENTORY_SUPPLIERS_DETAILS({
              id: value?.id,
            })}
            className="purple--text smallest-text-size"
          >
            {value?.name || "N/A"}
          </Link>
        ),
      },
      {
        Header: "Payment Status",
        accessor: "payment_status",
        Cell: ({ cell: { value } }) => (
          <span
            className={`${globalStyles[value === "PARTIALLY_PAID" ? "statusBlock--pending" : value === "PAID" ? "statusBlock--success" : "statusBlock--failed"]}`}
          >
            {value?.replace("_", " ") || "N/A"}
          </span>
        ),
      },

      {
        Header: "Amount Paid",
        accessor: "amount_paid_to_supplier",
        Cell: ({ cell: { value }, row }) =>
          formatCurrency({
            value: value,
            currencyCode: row?.original?.stores?.[0]?.currency_code,
          }),
      },
      {
        Header: "Outstanding",
        accessor: "outstanding_balance",
        Cell: ({ cell: { value }, row }) =>
          formatCurrency({
            value: value,
            currencyCode: row?.original?.stores?.[0]?.currency_code,
          }),
      },
      {
        Header: "Bill Amount",
        accessor: "total",
        Cell: ({ cell: { value }, row }) =>
          formatCurrency({
            value: value,
            currencyCode: row?.original?.stores?.[0]?.currency_code,
          }),
      },
      {
        Header: "Store",
        accessor: "stores",
        Cell: ({ cell: { value } }) =>
          value?.map((store) => store?.name)?.join(", ") || "N/A",
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <span className="d-flex align-items-center" style={{ gap: "10px" }}>
            {row?.original?.status === "DRAFT" ? (
              <secondaryComponents.NavigateWithPermission
                permissionParentKey={"Inventory Management"}
                permissionChildKey={"can_create_or_update_bill"}
              >
                <primaryComponents.Button
                  className="btn btn--primary smallBtn"
                  onClick={() => {
                    navigate(
                      pathConstants.INVENTORY_BILLS_EDIT({
                        id: row?.original?.id,
                      }),
                    );
                  }}
                >
                  Update Bill
                </primaryComponents.Button>
              </secondaryComponents.NavigateWithPermission>
            ) : null}

            {row?.original?.status === "APPROVED" ||
            row?.original?.status === "REJECTED" ||
            row?.original?.status === "IN_REVIEW" ? (
              <primaryComponents.Button
                className="btn btn--primary smallBtn"
                onClick={() => {
                  navigate(
                    pathConstants.INVENTORY_BILLS_DETAILS({
                      id: row?.original?.id,
                    }),
                  );
                }}
              >
                {row?.original?.status === "IN_REVIEW" ? "Review" : "View Bill"}
              </primaryComponents.Button>
            ) : null}

            {row?.original?.status !== "APPROVED" ? (
              <span
                className="cursor-pointer"
                onClick={() => setSelectedBillsDetails(row?.original)}
              >
                <DeleteIcon />
              </span>
            ) : null}

            <BillsExtra
              billsDetails={row?.original}
              btnStyles={{ width: "40px", height: "32px" }}
            />
          </span>
        ),
      },
    ],
    [],
  );

  const instance = useTable({
    columns,
    data: bills || [],
  });

  return {
    instance,
    selectedBillsDetails,
    setSelectedBillsDetails,
  };
};
